import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import portalApi, { portalToken } from "@/lib/portalApi";
import { MtgLogo } from "@/components/MtgLogo";
import { useSettings } from "@/context/SettingsContext";
import { PortalLogin } from "@/components/portal/PortalLogin";
import { PainChart } from "@/components/portal/PainChart";
import { CheckinForm } from "@/components/portal/CheckinForm";
import { PlanSummary } from "@/components/portal/PlanSummary";
import { Button } from "@/components/ui/button";
import { Activity, CalendarClock, LogOut, ShieldCheck, Loader2 } from "lucide-react";

const date = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const Card = ({ title, icon: Icon, children, testid }) => (
  <section className="rounded-2xl bg-white p-5 shadow-sm" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-slate-900"><Icon className="h-4 w-4 text-teal-600" /> {title}</div>
    {children}
  </section>
);

function PortalHome({ me, reload, logout }) {
  return (
    <div className="space-y-5" data-testid="portal-home">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-heading text-3xl font-extrabold text-white sm:text-4xl">Hi {(me.name || "").split(" ")[0]}</h1>
        <Button variant="ghost" onClick={logout} className="text-slate-300 hover:bg-white/10 hover:text-white" data-testid="portal-logout-btn"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
      </div>
      <Card title="Progress check-in" icon={Activity} testid="portal-checkin-card">
        {me.can_checkin ? <CheckinForm onDone={reload} />
          : <p className="text-sm text-slate-500" data-testid="portal-not-due">Thanks, you're all up to date.{me.next_due ? ` Your next check-in is due ${date(me.next_due)} — we'll email you a link.` : ""}</p>}
      </Card>
      <Card title="Your pain over time" icon={CalendarClock} testid="portal-chart-card"><PainChart checkins={me.checkins} /></Card>
      {me.plans.length > 0 && <div className="space-y-4">{me.plans.map((p) => <PlanSummary key={p.id} plan={p} />)}</div>}
    </div>
  );
}

export default function PortalPublic() {
  const { settings } = useSettings();
  const [params, setParams] = useSearchParams();
  const linkToken = params.get("t");
  const [me, setMe] = useState(null);
  const [state, setState] = useState(portalToken.get() ? "loading" : "login");

  const load = useCallback(() => {
    portalApi.get("/me").then((r) => { setMe(r.data); setState("in"); })
      .catch(() => { portalToken.clear(); setState("login"); });
  }, []);
  useEffect(() => { if (state === "loading") { load(); } }, [state, load]);

  const onIn = () => { if (linkToken) { setParams({}); } setState("loading"); };
  const logout = () => { portalToken.clear(); setMe(null); setState("login"); };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
        <div className="flex items-center justify-between">
          <MtgLogo withText name={settings.business_name} logoUrl={settings.logo_url} tagline={settings.tagline} />
          <div className="hidden items-center gap-2 text-xs text-slate-400 sm:flex"><ShieldCheck className="h-4 w-4 text-teal-400" /> Private patient portal</div>
        </div>
        <div className="py-8">
          {state === "loading" && <Loader2 className="mx-auto h-6 w-6 animate-spin text-teal-400" />}
          {state === "login" && (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="font-heading text-3xl font-extrabold text-white sm:text-4xl">Your progress portal</h1>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-300">Tell your clinician how you're getting on, see your plan and track your progress.</p>
              </div>
              <PortalLogin linkToken={state === "login" && me === null ? linkToken : null} onIn={onIn} />
            </div>
          )}
          {state === "in" && me && <PortalHome me={me} reload={load} logout={logout} />}
        </div>
        <p className="mx-auto max-w-2xl text-center text-[11px] leading-relaxed text-slate-500" data-testid="portal-privacy-notice">
          Your check-ins are shared only with {settings.business_name} clinicians and stored securely in line with UK GDPR.
          If your symptoms suddenly get much worse, contact the clinic or call NHS 111 — this portal is not monitored around the clock.
        </p>
      </div>
    </div>
  );
}
